import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import type { PresentButtonState } from '../model/console-view'

const Wrapper = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	gap: 8px;
	margin-right: 12px;
`

const Hint = styled.div`
	position: absolute;
	top: calc(100% + 8px);
	right: 0;
	z-index: 20;
	width: 300px;
	padding: 10px 14px;
	border-radius: 10px;
	background: ${colors.darkIndigo};
	color: ${colors.white};
	font-size: 13px;
	line-height: 1.4;
	box-shadow: 0 4px 16px ${colors.black15};
`

type Props = {
	state: PresentButtonState
	/** Offer full screen in this window too (browsers that cannot place it). */
	offerHere: boolean
	hintKey?: string
	/** Tooltip on Present, e.g. why there is no second screen. */
	presentTitle?: string
	onPresent: () => void
	onPresentHere: () => void
	onShowFullscreen: () => void
	onLeaveFullscreen: () => void
	onEnd: () => void
}

/**
 * The trainer's one control for presenter mode, in the room header next to
 * Exit. It stays in one place through the whole flow so the second click -
 * Show full screen - needs no mouse travel.
 */
export const PresentButton: FC<Props> = ({
	state,
	offerHere,
	hintKey,
	presentTitle,
	onPresent,
	onPresentHere,
	onShowFullscreen,
	onLeaveFullscreen,
	onEnd,
}) => (
	<Wrapper>
		{state === 'idle' ? (
			<>
				<ui.ButtonPrimary
					small
					onClick={onPresent}
					title={presentTitle}
					data-testid="presenter.present"
				>
					<intl.Translate>in_person.presenter.present</intl.Translate>
				</ui.ButtonPrimary>
				{offerHere ? (
					<ui.ButtonMinor
						small
						onClick={onPresentHere}
						data-testid="presenter.present_here"
					>
						<intl.Translate>in_person.presenter.present_here</intl.Translate>
					</ui.ButtonMinor>
				) : null}
			</>
		) : null}
		{state === 'windowed' ? (
			<>
				<ui.ButtonPrimary
					small
					onClick={onShowFullscreen}
					data-testid="presenter.show_full_screen"
				>
					<intl.Translate>in_person.presenter.show_full_screen</intl.Translate>
				</ui.ButtonPrimary>
				<ui.ButtonMinor small onClick={onEnd} data-testid="presenter.end">
					<intl.Translate>in_person.presenter.end</intl.Translate>
				</ui.ButtonMinor>
			</>
		) : null}
		{state === 'running' ? (
			<ui.ButtonMinor small onClick={onEnd} data-testid="presenter.end">
				<intl.Translate>in_person.presenter.end</intl.Translate>
			</ui.ButtonMinor>
		) : null}
		{state === 'here' ? (
			<ui.ButtonMinor
				small
				onClick={onLeaveFullscreen}
				data-testid="presenter.leave_full_screen"
			>
				<intl.Translate>in_person.presenter.leave_full_screen</intl.Translate>
			</ui.ButtonMinor>
		) : null}
		{hintKey ? (
			<Hint role="status" data-testid="presenter.hint">
				<intl.Translate>{hintKey}</intl.Translate>
			</Hint>
		) : null}
	</Wrapper>
)
